import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wifi, AlertTriangle, Clock } from "lucide-react";
import portalApi from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { useT } from "@/i18n";

export default function PortalDashboard() {
  const { user } = usePortalAuth();
  const t = useT();
  const p = t.portal;
  const [data, setData] = useState(null);

  useEffect(() => { portalApi.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const today = new Date().toISOString().slice(0, 10);

  if (!data) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 crm-card animate-pulse" />)}</div>;

  const activeSubs = data.subscriptions.filter((s) => s.status === "active");
  const overdueSubs = activeSubs.filter((s) => s.next_billing_date && s.next_billing_date < today);

  return (
    <div data-testid="portal-dashboard">
      <h1 className="crm-heading mb-1">{p.hello} {user?.name}</h1>
      <p className="text-sm mb-8" style={{ color: "var(--crm-text-muted)" }}>{p.welcome}</p>

      {overdueSubs.length > 0 && (
        <div className="crm-card p-4 mb-6 flex items-center gap-3" style={{ border: "2px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.05)" }}>
          <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: "#ef4444" }} />
          <div>
            <p className="text-sm font-bold" style={{ color: "#ef4444" }}>{p.overdueWarning.replace("{count}", overdueSubs.length)}</p>
            <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{p.overdueContact}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="crm-card p-5">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--crm-text-muted)" }}>{p.monthlyTotal}</p>
          <p className="text-2xl font-bold" style={{ color: "var(--crm-text)" }}>¥{data.monthly_total.toLocaleString()}</p>
        </div>
        <div className="crm-card p-5">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--crm-text-muted)" }}>{p.pending}</p>
          <p className="text-2xl font-bold" style={{ color: data.pending_amount > 0 ? "#f97316" : "var(--crm-text)" }}>¥{data.pending_amount.toLocaleString()}</p>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold" style={{ color: "var(--crm-text)" }}>{p.myServices}</h2>
          <Link to="/portal/services" className="text-xs font-medium" style={{ color: "var(--crm-accent)" }}>{p.viewAll}</Link>
        </div>
        {activeSubs.length === 0 ? (
          <div className="crm-card p-8 text-center" style={{ color: "var(--crm-text-dim)" }}>{p.noActiveService}</div>
        ) : (
          <div className="space-y-3">
            {activeSubs.map((s) => {
              const isOverdue = s.next_billing_date && s.next_billing_date < today;
              return (
                <div key={s.id} className="crm-card p-4 flex items-center gap-4" style={{ borderLeft: `3px solid ${isOverdue ? "#ef4444" : "#10b981"}` }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: isOverdue ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)", color: isOverdue ? "#ef4444" : "#10b981" }}>
                    <Wifi className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>{s.product_name}</p>
                    <p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>{p.nextBilling} {s.next_billing_date || "—"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>¥{s.monthly_amount.toLocaleString()}</p>
                    <p className="text-[10px]" style={{ color: "var(--crm-text-dim)" }}>{p.month}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {data.pending_payments.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold" style={{ color: "var(--crm-text)" }}>{p.pendingPayments}</h2>
            <Link to="/portal/payments" className="text-xs font-medium" style={{ color: "var(--crm-accent)" }}>{p.viewAll}</Link>
          </div>
          <div className="space-y-2">
            {data.pending_payments.slice(0, 5).map((pp) => {
              const isOverdue = pp.due_date < today;
              return (
                <div key={pp.id} className="crm-card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isOverdue ? <AlertTriangle className="w-4 h-4" style={{ color: "#ef4444" }} /> : <Clock className="w-4 h-4" style={{ color: "#f97316" }} />}
                    <div>
                      <p className="text-sm font-medium" style={{ color: "var(--crm-text)" }}>¥{pp.amount.toLocaleString()}</p>
                      <p className="text-xs" style={{ color: isOverdue ? "#ef4444" : "var(--crm-text-muted)" }}>{p.dueDate} {pp.due_date}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold"
                    style={{ background: isOverdue ? "rgba(239,68,68,0.12)" : "rgba(249,115,22,0.12)", color: isOverdue ? "#ef4444" : "#f97316" }}>
                    {isOverdue ? p.overdue : p.pendingLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
